import * as Sentry from "@sentry/react"
import {
    APPLICATION_KO_ORGANIZATIO_ID,
    SENTRY_CUSTOMER_NAME,
    SENTRY_DSN,
    SENTRY_ENVIRONMENT,
    SENTRY_TRACES_SAMPLE_RATE,
} from "../constants/constants"
import packageJson from "../../package.json"
import { getTenantFromHostname } from "./logrocket"

/**
 * Sentry initialisieren - muss ganz frueh in index.tsx aufgerufen werden.
 */
export function initSentry(): void {
    if (!SENTRY_DSN) {
        return
    }

    Sentry.init({
        dsn: SENTRY_DSN,
        environment: SENTRY_ENVIRONMENT,
        release: `ko-integrated@${packageJson.version}`,
        tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
        initialScope: {
            tags: {
                tenant_id: APPLICATION_KO_ORGANIZATIO_ID || getTenantFromHostname(),
                customer: SENTRY_CUSTOMER_NAME || "unknown",
            },
        },
        beforeSend(event, hint) {
            const error = hint.originalException

            // ResizeObserver-Fehler filtern (werden bereits global unterdrueckt)
            if (error instanceof Error && error.message?.includes("ResizeObserver")) {
                return null
            }
            if (typeof error === "string" && error.includes("ResizeObserver")) {
                return null
            }

            // iframe-resizer "Method not found"-Fehler filtern (harmlose Kollision
            // zwischen eigenen postMessages und dem iframe-resizer Protokoll)
            if (error instanceof Error && error.message?.includes("Error invoking post: Method not found")) {
                return null
            }

            // iframe-resizer: manche Android-WebViews (In-App-Browser) überschreiben
            // window.parent.postMessage, der Ready-Handshake scheitert dann harmlos
            const frames = event.exception?.values?.[0]?.stacktrace?.frames || []
            if (
                error instanceof Error &&
                error.message?.includes("postMessage is not a function") &&
                frames.some((frame) => frame.filename?.includes("iframeResizer"))
            ) {
                return null
            }

            // Cross-Origin Script-Fehler filtern (3rd Party, kein Stack Trace)
            if (event.message === "Script error." && !event.exception?.values?.[0]?.stacktrace?.frames?.length) {
                return null
            }

            // Maintenance-Fehler filtern (bereits separat behandelt)
            const errorMessage = event.exception?.values?.[0]?.value?.toLowerCase() || ""
            if (
                errorMessage.includes("service unavailable") ||
                errorMessage.includes("maintenance") ||
                errorMessage.includes("wartung")
            ) {
                return null
            }

            return event
        },
    })
}

/**
 * User-Kontext setzen bei Login. Nur die ID, keine E-Mail: die ID reicht zum Nachschlagen im Backend.
 */
export function setSentryUser(userId: string): void {
    Sentry.setUser({ id: userId })
}

/**
 * User-Kontext loeschen bei Logout.
 */
export function clearSentryUser(): void {
    Sentry.setUser(null)
}

/**
 * Erkennt erwartete Auth-Fehler (z.B. falsches Passwort, abgelaufener Login), die kein App-Bug sind.
 */
function isExpectedAuthError(message: string, code: string): boolean {
    return (
        message.includes("Wrong password") ||
        message.includes("User not found") ||
        // Backend thirdPartyLogin: unbekannte E-Mail (UserInputError, deutscher Text)
        message.includes("Benutzer nicht gefunden") ||
        // Backend LoginErrorMessages.NO_CUSTOMER_ACCOUNT: Mitarbeiter-Zugang ohne Kundenkonto (kein Bug)
        message.includes("No customer account") ||
        message.includes("Invalid credentials") ||
        code === "UNAUTHENTICATED"
    )
}

/**
 * GraphQL-Fehler mit Kontext an Sentry senden.
 */
export function captureGraphQLError(errors: readonly any[], operationName?: string, variables?: Record<string, any>): void {
    for (const err of errors) {
        const message = err.message || ""
        const code = err.extensions?.code || ""

        // Maintenance- und Schema-Fehler nicht melden, ebenso abgelehnte Rabattcodes: ein Tippfehler oder ein
        // abgelaufener Flyer-Code ist eine fachliche Antwort, kein Defekt (UI-Review 11.10.2026).
        if (
            code === "DISCOUNT_CODE_INVALID" ||
            code === "SERVICE_UNAVAILABLE" ||
            code === "MAINTENANCE" ||
            message.includes("Cannot query field") ||
            message.includes("Unknown field")
        ) {
            continue
        }

        // Erwartete Auth-Fehler nicht melden: Sentry zählt auch Warnungen gegen das Fehler-Kontingent,
        // und Login-Fehlschläge machten ~75 % des Volumens aus (Auswertung 02.11.2026).
        if (isExpectedAuthError(message, code)) {
            continue
        }

        Sentry.withScope((scope) => {
            scope.setTag("graphql_operation", operationName || "unknown")
            scope.setTag("graphql_error_code", code || "none")
            scope.setContext("graphql", {
                operationName,
                errorMessage: message,
                errorCode: code,
                // Nur die Feldnamen: die Werte (Namen, Adressen, IBAN, E-Mail) gehören nicht zu Sentry,
                // die vollständige Anfrage steht im Backend-Log.
                variableKeys: variables ? Object.keys(variables) : undefined,
            })
            Sentry.captureException(new Error(`GraphQL Error [${operationName}]: ${message}`))
        })
    }
}

/**
 * Sicherer Zugriff auf die erste GraphQL Error Message.
 * Ersetzt das unsichere Pattern: JSON.stringify(error.graphQLErrors[0].message, null, 4)
 */
export function getGraphQLErrorMessage(error: any): string | undefined {
    return error?.graphQLErrors?.[0]?.message ?? error?.networkError?.message ?? error?.message
}

/**
 * Sicherer Zugriff auf den `extensions.code` des ersten GraphQL-Errors
 * (verifiziertes Backend-Format, z.B. PRICE_QUOTE_CHANGED, WAITLIST_PAYMENT_NOT_ALLOWED).
 */
export function getGraphQLErrorCode(error: any): string | undefined {
    return error?.graphQLErrors?.[0]?.extensions?.code
}
